import { Body } from 'matter-js';

// Configuración de la fuerza de retorno al campo
const BOUNDS_PADDING_TOP = 0;       // (val > 0 ? esExterior : esInterior) Margen superior extra fuera de pantalla permitido
const BOUNDS_PADDING_BOTTOM = 0;    // (val > 0 ? esExterior : esInterior) Margen inferior extra fuera de pantalla permitido
const BOUNDS_PADDING_LEFT = 0;      // (val > 0 ? esExterior : esInterior) Margen izquierdo extra fuera de pantalla permitido
const BOUNDS_PADDING_RIGHT = 0;   // (val > 0 ? esExterior : esInterior) Margen derecho extra dentro de pantalla permitido
const STIFFNESS = 0.0006;           // Qué tan "fuerte" es el resorte de retorno
const MIN_FORCE = 0.001;            // Fuerza mínima para evitar que las entidades se queden atascadas en los bordes
const MAX_FORCE = 0.09;             // Fuerza máxima que se puede aplicar para evitar movimientos bruscos

// Exposición de la configuración para uso externo
export const _BOUNDARY_FORCE_CONFIG = {
    padding: {
        top: BOUNDS_PADDING_TOP,
        bottom: BOUNDS_PADDING_BOTTOM,
        left: BOUNDS_PADDING_LEFT,
        right: BOUNDS_PADDING_RIGHT,
    }
};

/**
 * Porcentaje del tamaño del cuerpo que se permite que salga
 * de los límites antes de aplicar la fuerza de retorno.
 *
 * - Min: -1: Permite que salga todo el cuerpo
 * - Max: 1: No permite que salga nada del cuerpo
 * - Se cuenta con que el cuerpo tenga translate(-50%, -50%), lo que ajusta visualmente al cuerpo.
 * */
const OUT_OF_BOUNDS_THRESHOLD = .9;

/**
 * Aplica una fuerza de retorno suave a los cuerpos que se salen de los límites del campo.
 *
 * Formula física aplicada: Ley de Hooke (simplificada):
 * `$$F = -k \cdot (posición - límite)$$`
 */
export const applySoftBounds = (bodies: Body[], width: number, height: number, ignoreCategories: number[] = []) => {
    bodies.forEach((body) => {
        if (body.isStatic) return;
        if (ignoreCategories.includes(body.collisionFilter.category)) return;

        const { x, y } = body.position; // Centro del cuerpo físico
        const radius = body.circleRadius ? body.circleRadius : 0; // Asume cuerpos circulares...
        let forceX = 0;
        let forceY = 0;

        // Límite izquierdo
        if (x < BOUNDS_PADDING_LEFT + radius * OUT_OF_BOUNDS_THRESHOLD) {
            const distance = (BOUNDS_PADDING_LEFT + radius * OUT_OF_BOUNDS_THRESHOLD) - x;
            forceX = STIFFNESS * distance;
        }
        // Límite derecho
        else if (x > width + BOUNDS_PADDING_RIGHT - radius * OUT_OF_BOUNDS_THRESHOLD) {
            const distance = x - (width + BOUNDS_PADDING_RIGHT - radius * OUT_OF_BOUNDS_THRESHOLD);
            forceX = -STIFFNESS * distance;
        }

        // Límite superior
        if (y < BOUNDS_PADDING_TOP + radius * OUT_OF_BOUNDS_THRESHOLD) {
            const distance = (BOUNDS_PADDING_TOP + radius * OUT_OF_BOUNDS_THRESHOLD) - y;
            forceY = STIFFNESS * distance;
        }
        // Límite inferior
        else if (y > height + BOUNDS_PADDING_BOTTOM - radius * OUT_OF_BOUNDS_THRESHOLD) {
            const distance = y - (height + BOUNDS_PADDING_BOTTOM - radius * OUT_OF_BOUNDS_THRESHOLD);
            forceY = -STIFFNESS * distance;
        }

        // Limitar la fuerza máxima para evitar movimientos bruscos
        forceX = Math.max(-MAX_FORCE, Math.min(MAX_FORCE, forceX));
        forceY = Math.max(-MAX_FORCE, Math.min(MAX_FORCE, forceY));

        // Aplicar una fuerza mínima para evitar que las entidades se queden atascadas en los bordes
        if (Math.abs(forceX) < MIN_FORCE && Math.abs(forceX) > 0) {
            forceX = forceX < 0 ? -MIN_FORCE : MIN_FORCE;
        }
        if (Math.abs(forceY) < MIN_FORCE && Math.abs(forceY) > 0) {
            forceY = forceY < 0 ? -MIN_FORCE : MIN_FORCE;
        }

        // Aplicar la fuerza resultante si es necesario
        if (forceX !== 0 || forceY !== 0) {
            Body.applyForce(body, body.position, { x: forceX, y: forceY });
        }
    });
};